import { useCallback, useMemo } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatCpf } from "../../../shared/utils/cpf";
import type { AuthUser } from "../../auth/shared/types/auth";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  isUserEligibleForChallengeRule,
  normalizeCategoryValue,
  normalizeSexValue,
  resolveChallengeCategoryForUser,
  resolveChallengeGender,
} from "../../challenges/utils/challengeEligibility";
import {
  normalizeCpf,
  normalizeCpfFromPlayerInput,
} from "../../challenges/utils/challengePairs";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";
import { normalizeTournamentRegistrationCategoriesForEdit } from "../utils/tournamentCategories";

type UseAdminChallengeParticipantsInput = {
  activeRegistrationCategoryKeyForModal: string;
  challengeDetail: ChallengeDetail | null;
  challengeEventRegistrations: AdminEventRegistration[];
  currentPairMode: PairMode;
  draftPairs: DraftPair[];
  draftPlayers: DraftPlayer[];
  tournamentPairModalRegistrationId?: number | null;
  tournamentPairModalCategoryKey?: string | null;
  users: AuthUser[];
};

export function useAdminChallengeParticipants({
  activeRegistrationCategoryKeyForModal,
  challengeDetail,
  challengeEventRegistrations,
  currentPairMode,
  draftPairs,
  draftPlayers,
  tournamentPairModalRegistrationId,
  tournamentPairModalCategoryKey,
  users,
}: UseAdminChallengeParticipantsInput) {
  const missingRegistrationDataForUser = useCallback(
    (user?: AuthUser | null) => {
      const missing: string[] = [];
      if (!user) return ["usuário"];
      const safeSex = normalizeSexValue(user.sex);
      const safeCategory = normalizeCategoryValue(user.category);

      if (safeSex !== "male" && safeSex !== "female") missing.push("sexo");
      if (!safeCategory) missing.push("categoria");
      return missing;
    },
    [],
  );

  const selectedCpfsSet = useMemo(() => {
    const cpfs =
      currentPairMode === "fixed"
        ? draftPairs.flatMap((pair) => [pair.cpf1, pair.cpf2])
        : draftPlayers.map((player) => player.cpf);
    return new Set(
      cpfs.map((item) => normalizeCpf(item)).filter((cpf) => cpf.length >= 8),
    );
  }, [currentPairMode, draftPairs, draftPlayers]);

  const eligibleUsersForChallenge = useMemo(() => {
    if (!challengeDetail) return users;
    const isTournament =
      String(challengeDetail.challengeType || "")
        .trim()
        .toLowerCase() === "tournament";
    const challengeType = String(challengeDetail.challengeType || "")
      .trim()
      .toLowerCase();
    const hasRegistrationCategories =
      isTournament ||
      (["challenge", "poco"].includes(challengeType) &&
        Array.isArray(challengeDetail.registrationCategories) &&
        challengeDetail.registrationCategories.length > 0);
    const safeActiveCategoryKey = String(
      activeRegistrationCategoryKeyForModal || "",
    ).trim();
    const tournamentCategories =
      normalizeTournamentRegistrationCategoriesForEdit(
        challengeDetail.registrationCategories,
      );
    const activeTournamentCategoryRule = hasRegistrationCategories
      ? tournamentCategories.find(
          (item) => item.categoryKey === safeActiveCategoryKey,
        ) ||
        tournamentCategories[0] ||
        null
      : null;
    const challengeGender = resolveChallengeGender(
      hasRegistrationCategories
        ? activeTournamentCategoryRule?.categoryGender ||
            challengeDetail.categoryGender
        : challengeDetail.categoryGender,
    );
    const challengeCategory = normalizeCategoryValue(
      hasRegistrationCategories ? null : challengeDetail.category,
    );
    const challengeCategoryMale = normalizeCategoryValue(
      hasRegistrationCategories
        ? activeTournamentCategoryRule?.categoryMale
        : challengeDetail.categoryMale,
    );
    const challengeCategoryFemale = normalizeCategoryValue(
      hasRegistrationCategories
        ? activeTournamentCategoryRule?.categoryFemale
        : challengeDetail.categoryFemale,
    );
    return users.filter((user) => {
      const cpf = normalizeCpf(user.cpf);
      if (cpf.length < 8) return false;
      const role = String(user.role || "")
        .trim()
        .toLowerCase();
      if (!(role === "user" || role === "organizer")) return false;
      if (missingRegistrationDataForUser(user).length > 0) return false;
      const userSex = normalizeSexValue(user.sex);
      const userCategory = normalizeCategoryValue(user.category);
      const requiredCategory = resolveChallengeCategoryForUser({
        challengeGender,
        challengeCategory,
        challengeCategoryMale,
        challengeCategoryFemale,
        userSex,
      });
      return isUserEligibleForChallengeRule({
        userSex,
        userCategory,
        challengeGender,
        challengeCategory: requiredCategory,
      });
    });
  }, [
    activeRegistrationCategoryKeyForModal,
    challengeDetail,
    missingRegistrationDataForUser,
    users,
  ]);

  const eligibleCpfSet = useMemo(
    () =>
      new Set(
        eligibleUsersForChallenge
          .map((user) => normalizeCpf(user.cpf))
          .filter(Boolean),
      ),
    [eligibleUsersForChallenge],
  );

  const availableUsersForChallenge = useMemo(
    () =>
      eligibleUsersForChallenge.filter((user) => {
        const cpf = normalizeCpf(user.cpf);
        if (cpf.length < 8) return false;
        return !selectedCpfsSet.has(cpf);
      }),
    [eligibleUsersForChallenge, selectedCpfsSet],
  );

  const tournamentPairModalBlockedCpfs = useMemo(() => {
    const categoryKey = String(tournamentPairModalCategoryKey || "").trim();
    if (!categoryKey) return new Set<string>();
    const editingRegistrationId = Number(tournamentPairModalRegistrationId || 0);
    const blocked = new Set<string>();
    challengeEventRegistrations.forEach((row) => {
      if (row.status === "canceled") return;
      if (
        Number.isFinite(editingRegistrationId) &&
        editingRegistrationId > 0 &&
        Number(row.id) === editingRegistrationId
      ) {
        return;
      }
      if (String(row.registrationCategoryKey || "").trim() !== categoryKey) {
        return;
      }
      [row.userCpf, row.partnerCpf].forEach((value) => {
        const cpf = normalizeCpf(value);
        if (cpf.length >= 8) blocked.add(cpf);
      });
    });
    return blocked;
  }, [
    challengeEventRegistrations,
    tournamentPairModalCategoryKey,
    tournamentPairModalRegistrationId,
  ]);

  const availableUsersForTournamentPairModal = useMemo(
    () =>
      users.filter((user) => {
        const cpf = normalizeCpf(user.cpf);
        if (cpf.length < 8) return false;
        if (!['user', 'organizer', 'admin'].includes(String(user.role || '').trim().toLowerCase())) return false;
        if (missingRegistrationDataForUser(user).length > 0) return false;
        return !tournamentPairModalBlockedCpfs.has(cpf);
      }),
    [users, missingRegistrationDataForUser, tournamentPairModalBlockedCpfs],
  );

  const userByCpf = useMemo(() => {
    const map: Record<string, AuthUser> = {};
    users.forEach((user) => {
      const cpf = normalizeCpf(user.cpf);
      if (!cpf) return;
      map[cpf] = user;
    });
    return map;
  }, [users]);

  const resolveChallengePlayerCpfInput = useCallback(
    (value: string) => {
      const raw = String(value || "").trim();
      const cpf = normalizeCpfFromPlayerInput(raw);
      if (cpf && userByCpf[cpf]) return cpf;

      const normalizedName = raw
        .replace(/\s+-\s+\d[\d.\-\s]*$/, "")
        .trim()
        .toLowerCase();
      if (!normalizedName) return cpf;

      const matched = availableUsersForChallenge.find(
        (user) =>
          String(user.name || "")
            .trim()
            .toLowerCase() === normalizedName,
      );
      return matched ? normalizeCpf(matched.cpf) : cpf;
    },
    [availableUsersForChallenge, userByCpf],
  );

  const explainIneligibleUserByCpf = useCallback(
    (cpf: string) => {
      const normalizedCpf = normalizeCpf(cpf);
      const user = userByCpf[normalizedCpf];
      if (!user) {
        return `${siteTexts.admin.registrationsTab.player} CPF ${formatCpf(
          normalizedCpf,
        )} ${siteTexts.admin.registrationsTab.notFound}.`;
      }

      const challengeType = String(challengeDetail?.challengeType || "")
        .trim()
        .toLowerCase();
      const hasRegistrationCategories =
        challengeType === "tournament" ||
        (["challenge", "poco"].includes(challengeType) &&
          Array.isArray(challengeDetail?.registrationCategories) &&
          challengeDetail.registrationCategories.length > 0);
      const safeActiveCategoryKey = String(
        activeRegistrationCategoryKeyForModal || "",
      ).trim();
      const tournamentCategories =
        normalizeTournamentRegistrationCategoriesForEdit(
          challengeDetail?.registrationCategories,
        );
      const activeTournamentCategoryRule = hasRegistrationCategories
        ? tournamentCategories.find(
            (item) => item.categoryKey === safeActiveCategoryKey,
          ) ||
          tournamentCategories[0] ||
          null
        : null;

      const challengeGender = resolveChallengeGender(
        hasRegistrationCategories
          ? activeTournamentCategoryRule?.categoryGender ||
              challengeDetail?.categoryGender
          : challengeDetail?.categoryGender,
      );
      const challengeCategory = normalizeCategoryValue(
        hasRegistrationCategories ? null : challengeDetail?.category,
      );
      const challengeCategoryMale = normalizeCategoryValue(
        hasRegistrationCategories
          ? activeTournamentCategoryRule?.categoryMale
          : challengeDetail?.categoryMale,
      );
      const challengeCategoryFemale = normalizeCategoryValue(
        hasRegistrationCategories
          ? activeTournamentCategoryRule?.categoryFemale
          : challengeDetail?.categoryFemale,
      );

      const userSex = normalizeSexValue(user.sex);
      const userCategory = normalizeCategoryValue(user.category);
      const requiredCategory = resolveChallengeCategoryForUser({
        challengeGender,
        challengeCategory,
        challengeCategoryMale,
        challengeCategoryFemale,
        userSex,
      });

      const safeName =
        String(user.name || "").trim() || siteTexts.challengeEngine.labels.player;
      const missingFields = missingRegistrationDataForUser(user);
      if (missingFields.length > 0) {
        return `${safeName}: não pode ser adicionado. Cadastro incompleto (${missingFields.join(
          ", ",
        )}).`;
      }

      if (challengeGender && userSex !== "male" && userSex !== "female") {
        return `${safeName}: cadastro incompleto (sem sexo definido).`;
      }
      if (!userCategory) {
        return `${safeName}: cadastro incompleto (sem categoria definida).`;
      }
      if (
        challengeGender !== "mixed" &&
        challengeGender &&
        userSex !== challengeGender
      ) {
        return `${safeName}: sexo incompatível com este desafio.`;
      }
      if (
        requiredCategory &&
        !isUserEligibleForChallengeRule({
          userSex,
          userCategory,
          challengeGender,
          challengeCategory: requiredCategory,
        })
      ) {
        return `${safeName}: categoria incompatível com este desafio.`;
      }
      return `${safeName}: não atende à regra deste desafio.`;
    },
    [
      activeRegistrationCategoryKeyForModal,
      challengeDetail,
      missingRegistrationDataForUser,
      userByCpf,
    ],
  );

  const nameByCpf = useMemo(() => {
    const map: Record<string, string> = {};
    users.forEach((user) => {
      const cpf = normalizeCpf(user.cpf);
      if (!cpf) return;
      map[cpf] =
        String(user.name || "").trim() || siteTexts.challengeEngine.labels.player;
    });
    (challengeDetail?.participants || []).forEach((participant) => {
      const cpf = normalizeCpf(participant.cpf);
      if (!cpf) return;
      if (!map[cpf]) {
        map[cpf] =
          String(participant.name || "").trim() ||
          siteTexts.challengeEngine.labels.player;
      }
    });
    return map;
  }, [challengeDetail?.participants, users]);

  return {
    availableUsersForChallenge,
    availableUsersForTournamentPairModal,
    eligibleCpfSet,
    explainIneligibleUserByCpf,
    missingRegistrationDataForUser,
    nameByCpf,
    resolveChallengePlayerCpfInput,
    selectedCpfsSet,
    userByCpf,
  };
}
